import { CONNECT_CLIENT } from "./definitions";
import { buildAnalytics4Products, getTotalRevenue, getTotalTaxes } from "./gtmInntopia";
import { getCustomerId } from "./localStorage";

const formatDateAsISO = (value) => {
  if (!value) return undefined;

  if (typeof value === "string") {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) return undefined;

    return `${parsedDate.getFullYear()}-${String(parsedDate.getMonth() + 1).padStart(2, "0")}-${String(parsedDate.getDate()).padStart(2, "0")}`;
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
  }

  return undefined;
};

const buildCustomEventParameters = (customParameters = {}) => {
  const eventParameters = {};
  const startDate = formatDateAsISO(customParameters.start_date);
  const endDate = formatDateAsISO(customParameters.end_date);

  if (startDate) {
    eventParameters.start_date = startDate;
  }

  if (endDate) {
    eventParameters.end_date = endDate;
  }

  return eventParameters;
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeProductFees = (product = {}) => {
  if (Array.isArray(product.fees)) return product.fees;
  if (product.fee) return [product.fee];
  return [];
};

const getProductFeesTotal = (product = {}, quantityInput) => {
  const quantity = toNumber(quantityInput ?? product.quantity ?? product.Quantity ?? 1);
  const feesTotal = normalizeProductFees(product).reduce(
    (sum, fee) => sum + toNumber(fee?.value),
    0
  );

  return feesTotal * quantity;
};

const getPriceAmount = (price) => {
  if (Array.isArray(price)) return getPriceAmount(price[0]);
  if (!price || typeof price !== "object") return Number.isFinite(Number(price)) ? Number(price) : undefined;
  const value = price.amount ?? price.price_value ?? price.price ?? price.unitPrice ?? price.value;
  return Number.isFinite(Number(value)) ? Number(value) : undefined;
};

const getProductPrice = (product = {}) => {
  const prices = [
    product.unit_price,
    product.unitPrice,
    product.UnitaryPrice,
    product.price,
    product.CurrencyAware_Price?.QuoteAmount,
    product.CurrencyAware_PackagePrice?.QuoteAmount,
    product.Pricing?.price?.perStay,
    product.Supplier?.Pricing?.price?.perStay,
    product.Price,
    product.FromPrice,
  ];

  for (const price of prices) {
    const amount = getPriceAmount(price);
    if (amount !== undefined) return Math.round(amount * 100) / 100;
  }

  return 0;
};

const getPriceCurrency = (price) => {
  if (Array.isArray(price)) return getPriceCurrency(price[0]);
  if (!price || typeof price !== "object") return "";
  return price.currency || price.Currency || price.BaseCurrencyCode || "";
};

const getGoogleTagManagerRevenue = (prods) => {
  if (!Array.isArray(prods)) return 0;

  return prods.reduce((total, prod) => {
    const quantity = toNumber(prod.quantity ?? prod.Quantity ?? 1) || 1;
    return total + (getProductPrice(prod) * quantity) + getProductFeesTotal(prod, quantity);
  }, 0);
};
 
 const buildGoogleTagManagerProducts = (event, prods, section = false) => {
   let gtmProducts = [];
   prods &&
     prods.forEach((prod, index) => {
       let gtmProduct = {};
       Object.keys(prod).forEach(key => {
         if (key === "name" || key === "product_name") gtmProduct.item_name = prod[key];
         if (key === "id" || key === "uuid" || key === "product_id") gtmProduct.item_id = prod[key];
         if (key === "unit_price" || key === "unitPrice") gtmProduct.price = prod[key];
         if (key === "price") {
          // gausta e-commerce assignations
          if (Array.isArray(prod.price)) {
            gtmProduct.price = getProductPrice(prod);
            gtmProduct.currency = getPriceCurrency(prod.price);
            gtmProduct.item_variant = prod.price[0].price_name
          } else {
            gtmProduct.price = getProductPrice(prod);
            gtmProduct.currency = getPriceCurrency(prod.price);
            gtmProduct.item_variant = prod.price.price_name
          }
         }
         if (key === "unit_price_currency" || key === "currency") gtmProduct.currency = prod[key];
         if (key === "variant" || key === "product_type" || key === "type") gtmProduct.item_variant = prod[key];
 
         if (key === "productCategory" && prod.productCategory.name) gtmProduct.item_category = prod.productCategory.name
         if (key === "category") gtmProduct.item_category = prod[key];
         
         if (key === "supplier" && prod.supplier?.legalName) gtmProduct.item_brand = prod.supplier.legalName
       })
 
       if (section) gtmProduct.list_name = section;
       if (
         event === "view_item_list" ||
         event === "select_item" ||
         event === "view_item"
       ) {
         gtmProduct.index = index;
       }
 
       gtmProducts.push(gtmProduct);
     });
 
   return gtmProducts;
 };
 
 const getRevenue = (prods) => {
   let revenue = 0;
   prods.forEach((prod) => {
     const quantity = toNumber(prod.quantity ?? 1);
     revenue += quantity * toNumber(prod.unitPrice);
     revenue += getProductFeesTotal(prod, quantity);
   });
   return revenue;
 };
 
 const getTaxes = (prods) => {
   let taxes = 0;
   prods.forEach((prod) => (taxes += prod.quantity * (prod.taxAmount ? prod.taxAmount : 0)));
   return taxes;
 };

 const getCurrency = (prods) => {
  let currency = "";
  if (prods && prods[0] && prods[0].currency) currency = prods[0].currency;
  if (prods && prods[0] && prods[0].Currency) currency = prods[0].Currency;
  if (prods && prods[0] && prods[0].price) currency = getPriceCurrency(prods[0].price) || currency;
  if (prods && prods[0] && prods[0].CurrencyAware_Price && prods[0].CurrencyAware_Price.BaseCurrencyCode) currency = prods[0].CurrencyAware_Price.BaseCurrencyCode;
  if (prods && prods[0] && prods[0].CurrencyAware_PackagePrice && prods[0].CurrencyAware_PackagePrice.BaseCurrencyCode) currency = prods[0].CurrencyAware_PackagePrice.BaseCurrencyCode;
  return currency;
 }
const getCustomerIdentifier = () => {
  try {
    return getCustomerId() || null;
  } catch (e) {
    return null;
  }
};

export const gtmFunction = (event, data, section = false, integration = "", customParameters = {}) => {
  let gtmObject = {};
  const eventParameters = buildCustomEventParameters(customParameters);
  switch (event) {
    case "view_item_list":
      gtmObject = {
        event,
        ...eventParameters,
        ecommerce: {
          items: integration === "inntopia" ? buildAnalytics4Products(data, section, event) : buildGoogleTagManagerProducts(event, data, section),
        },
      };
      break;
    case "select_item":
      gtmObject = {
        event,
        ...eventParameters,
        ecommerce: {
          items: integration === "inntopia" ? buildAnalytics4Products(data, section, event) : buildGoogleTagManagerProducts(event, data),
        },
      };
      break;
    case "view_item":
      gtmObject = {
        event,
        ...eventParameters,
        ecommerce: {
          value: integration === "inntopia" ? getTotalRevenue(data) : getGoogleTagManagerRevenue(data),
          currency: getCurrency(data), 
          items: integration === "inntopia" ? buildAnalytics4Products(data, section, event) : buildGoogleTagManagerProducts(event, data),
        },
      };
      console.log("view_item final payload", gtmObject)
      break;
    case "add_to_cart":
      gtmObject = {
        event,
        ...eventParameters,
        ecommerce: {
          currency: getCurrency(data), 
          value: getTotalRevenue(data),
          items: integration === "inntopia" ? buildAnalytics4Products(data, section, event) : buildGoogleTagManagerProducts(event, data),
        },
      };
      break;
    case "view_cart":
      gtmObject = {
        event,
        ...eventParameters,
        ecommerce: {
          currency: getCurrency(data), 
          value: getTotalRevenue(data),
          items: integration === "inntopia" ? buildAnalytics4Products(data, section, event) : buildGoogleTagManagerProducts(event, data),
        },
      };
      break;
    case "remove_from_cart":
      gtmObject = {
        event,
        ...eventParameters,
        ecommerce: {
          value: getTotalRevenue(data),
          items: integration === "inntopia" ? buildAnalytics4Products(data, section, event) : buildGoogleTagManagerProducts(event, data),
        },
      };
      break;
    case "begin_checkout":
      console.log("begin_checkout", getTotalRevenue(data))
      gtmObject = {
        event,
        ...eventParameters,
        ecommerce: {
          currency: getCurrency(data),
          value: getTotalRevenue(data),
          items: integration === "inntopia" ? buildAnalytics4Products(data, section, event) : buildGoogleTagManagerProducts(event, data),
        },
      };
      break;
    case "add_payment_info":
        gtmObject = {
          event,
          ...eventParameters,
          ecommerce: {
            currency: getCurrency(data),
            value: getTotalRevenue(data),
            items: integration === "inntopia" ? buildAnalytics4Products(data, section, event) : buildGoogleTagManagerProducts(event, data),
          },
        };
        break;
    case "purchase":
      gtmObject = {
        event,
        ...eventParameters,
        ecommerce: {
          currency: getCurrency(data.products),
          transaction_id: data.id,
          affiliation: CONNECT_CLIENT,
          value: integration === "inntopia" ? getTotalRevenue(data.products) : getRevenue(data.products),
          tax: integration === "inntopia" ? getTotalTaxes(data.products) : getTaxes(data.products),
          shipping: 0,
          items: integration === "inntopia" ? buildAnalytics4Products(data.products, section, event) : buildGoogleTagManagerProducts(event, data.products),
        },
      };
      break;
    case "view_login":
      gtmObject = {
        event,
        ...eventParameters,
        uuid: data.uuid,
      };
      break;
    case "attempt_login":
      gtmObject = {
        event,
        ...eventParameters,
        uuid: data.uuid,
        email: data.email,
      };
      break;
    case "successful_login":
      gtmObject = {
        event,
        ...eventParameters,
        uuid: data.uuid,
        email: data.email,
        user_id: getCustomerIdentifier(),
      };
      break;
    case "sign_up":
      gtmObject = {
        event,
        ...eventParameters,
        user_id: getCustomerIdentifier(),
      };
      break;
    case "logout":
      gtmObject = {
        event,
        ...eventParameters,
        user_id: null,
      };
      break;
    case "view_reset_password":
      gtmObject = {
        event,
        ...eventParameters,
        uuid: data.uuid,
      };
      break;
    case "attempt_reset_password":
      gtmObject = {
        event,
        ...eventParameters,
        uuid: data.uuid,
      };
      break;
    case "successful_reset_password":
      gtmObject = {
        event,
        ...eventParameters,
        uuid: data.uuid,
      };
      break;
    case "page_view":
      gtmObject = {
        event,
        ...eventParameters,
        user_id: getCustomerIdentifier(),
      };
      break;
    default:
      break;
  }

  if (
    typeof window !== "undefined" &&
    typeof window.dataLayer !== "undefined"
  ) {
    if (gtmObject.ecommerce) {
      window.dataLayer.push({ ecommerce: null });
    }
    window.dataLayer.push(gtmObject);
  }
};
